import { useEffect, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent } from 'react';
import { useLongPress } from '../../ui/longPress';
import { Menu, type MenuEntry } from '../../ui/menu';
import { tip } from '../../ui/tooltip';
import { tileUrl } from '../content/tileUrl';
import { restingTakes } from './takesOrder';

/*
 * A shape's takes — every result its runs have given it — as a pile of small cards (Tarek, 14 Oct:
 * "show me the options as small boxes stacked on top of each other … when i hover on top of them
 * they open up and i can click one of them"). It replaced "‹ Image 5 / 5 ›" and the fill picker's
 * grid, which grew a row for every few runs.
 *
 * At rest: the takes on one line, overlapping — oldest to newest, left to right (Tarek, 2 Nov), so
 * Take N is the Nth card and a new run lands on the right — the shown one in front of its neighbours,
 * and "+N" before the fifth last for the older ones (Tarek, 14 Oct: "same line horizontal stack", after
 * a tilted pile with a count badge). Pointed at, the same line spreads out, in the same order, so
 * nothing changes order while choosing; a press shows that take. Keep This keeps the shown take
 * and forgets the rest (they stay in My Stuff). A right press on a take offers the same, and
 * Place as New Shape where the host can make one.
 */

export interface Take { id: string; src: string; video: boolean }

const CARD = 32, STEP = 36, OVERLAP = 11, MORE = 28;

export function TakesStack({ takes, active, columns = 10, align = 'center', onPick, onKeep, onPlaceBeside }: {
  takes: readonly Take[]; active: number; columns?: number; align?: 'center' | 'start';
  onPick(index: number): void; onKeep(index: number): void; onPlaceBeside?(index: number): void;
}) {
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState<{ index: number; anchor: HTMLElement } | null>(null);
  const leaving = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(leaving.current), []);
  useEffect(() => {
    if (!open) return;
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape' && !menu) { event.stopPropagation(); setOpen(false); } };
    window.addEventListener('keydown', escape, true);
    return () => window.removeEventListener('keydown', escape, true);
  }, [open, menu]);
  const stay = () => { clearTimeout(leaving.current); setOpen(true); };
  const go = () => { clearTimeout(leaving.current); leaving.current = setTimeout(() => { if (!menu) setOpen(false); }, 220); };
  // A finger's long-press is the right press (the responsive pass, 2026-10-26): Place as New Shape was only there.
  const held = useRef(0);
  const hold = useLongPress<HTMLButtonElement>((element) => { stay(); setMenu({ index: held.current, anchor: element }); });
  const holdFor = (index: number) => ({
    onPointerDown: (event: ReactPointerEvent<HTMLButtonElement>) => { held.current = index; hold.onPointerDown(event); },
    onPointerMove: hold.onPointerMove, onPointerUp: hold.onPointerUp, onPointerCancel: hold.onPointerCancel, onClickCapture: hold.onClickCapture,
  });

  const count = takes.length;
  const cols = Math.max(1, Math.min(columns, count)), rows = Math.ceil(count / cols);
  const width = cols * STEP - (STEP - CARD), height = rows * STEP - (STEP - CARD);
  const atRest = restingTakes(count, active);
  const resting = atRest.length, more = count - resting;
  const lead = more ? MORE : 0, restWidth = lead + CARD + (resting - 1) * OVERLAP;
  const left0 = align === 'center' ? -width / 2 : 0, closedLeft = align === 'center' ? -restWidth / 2 : 0;
  // The bar under an open pile is at least wide enough for its words.
  const barWidth = Math.max(width, 168), barLeft = align === 'center' ? -barWidth / 2 : 0;
  const placeOf = (index: number): CSSProperties => {
    if (open) {
      return { transform: `translate(${left0 + (index % cols) * STEP}px, ${Math.floor(index / cols) * STEP}px)`, transitionDelay: `${Math.min(index, 16) * 12}ms`, zIndex: index + 1, opacity: 1 };
    }
    const at = atRest.indexOf(index);
    if (at < 0) return { transform: `translate(${closedLeft + lead}px, 0)`, zIndex: 0, opacity: 0, pointerEvents: 'none' };
    // One line, each card a little to the right of the one before; the shown take in front.
    return { transform: `translate(${closedLeft + lead + at * OVERLAP}px, 0)`, zIndex: index === active ? count + 1 : at + 1, opacity: 1 };
  };
  const items = (index: number): MenuEntry[] => [
    { id: 'show', label: 'Show This', disabled: index === active, onSelect: () => onPick(index) },
    { id: 'keep', label: 'Keep This', note: 'The other takes leave this shape. They stay in My Stuff.', onSelect: () => onKeep(index) },
    ...(onPlaceBeside ? [{ kind: 'separator' as const }, { id: 'beside', label: 'Place as New Shape', note: 'A copy of the shape beside it, with this take.', onSelect: () => onPlaceBeside(index) }] : []),
  ];

  return <div className={`artwork-takes${open ? ' is-open' : ''}${align === 'start' ? ' is-start' : ''}`} onPointerEnter={stay} onPointerLeave={go} onFocus={stay}
    onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) go(); }}>
    {/* Holds the pointer across the gaps between cards, closed or open. */}
    <span className="artwork-takes-hit" aria-hidden style={open ? { left: Math.min(left0, barLeft) - 8, width: barWidth + 16, height: height + 52 } : { left: closedLeft - 8, width: restWidth + 16, height: CARD + 16 }} />
    <div className="artwork-takes-cards" role="listbox" aria-label={`${count} takes`}>
      {takes.map((take, index) => <button key={take.id} type="button" role="option" aria-selected={index === active}
        aria-label={`Take ${index + 1} of ${count}${index === active ? ', shown' : ''}`} tabIndex={open || index === active ? 0 : -1}
        className={`artwork-take${index === active ? ' is-active' : ''}`} style={placeOf(index)}
        onClick={() => open ? onPick(index) : setOpen(true)}
        onContextMenu={event => { event.preventDefault(); stay(); setMenu({ index, anchor: event.currentTarget }); }}
        {...holdFor(index)}>
        {take.video ? <video src={`${take.src}#t=0.1`} muted playsInline preload="metadata" /> : <img src={tileUrl(take.src)} alt="" draggable={false} loading="lazy" />}
      </button>)}
      {!open && more > 0 && <span className="artwork-takes-more" style={{ left: closedLeft, width: MORE - 6, textAlign: 'right' }} aria-hidden>+{more}</span>}
    </div>
    {/* Over what is below, never pushing it: the pile keeps one card's height in the layout. */}
    {open && <div className="artwork-takes-bar" style={{ top: height + 10, left: barLeft, width: barWidth }}>
      <span>Take {active + 1} of {count}</span>
      <button type="button" {...tip('The other takes leave this shape. They stay in My Stuff.')} onClick={() => onKeep(active)}>Keep This</button>
    </div>}
    <Menu open={!!menu} anchor={menu?.anchor ?? null} onClose={() => { setMenu(null); go(); }} items={menu ? items(menu.index) : []} placement="below" align="start" label="Take" width={240} />
  </div>;
}
